import * as THREE from 'three';
import type { Network } from '../data/types';
import { edgeTo3D } from '../geometry/paths';

/**
 * Renders physical structure bodies (tunnel shells, bridge decks) separately
 * from the track centerlines. Tunnel shells are translucent so trains inside
 * remain visible. Approximation, not a structural survey.
 */
export class StructuresLayer {
  readonly group = new THREE.Group();
  private tunnelMeshes: THREE.Mesh[] = [];

  constructor() {
    this.group.name = 'structures';
  }

  build(network: Network, nodeHeights: Map<string, number>) {
    for (const edge of network.edges) {
      if (edge.coords.length < 2) continue;
      if (edge.structure === 'tunnel') {
        this.addTunnel(edge, nodeHeights);
      } else if (edge.structure === 'bridge') {
        this.addBridge(edge, nodeHeights);
      }
    }
  }

  private addTunnel(edge: Network['edges'][number], nodeHeights: Map<string, number>) {
    const pts = edgeTo3D(edge, nodeHeights);
    const vec = pts.map((p) => new THREE.Vector3(p.x, p.y, p.z));
    const curve = new THREE.CatmullRomCurve3(vec, false, 'catmullrom', 0.5);
    const geo = new THREE.TubeGeometry(curve, Math.max(10, pts.length * 2), 5.5, 12, false);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x3a5f8a,
      transparent: true,
      opacity: 0.16,
      roughness: 0.9,
      metalness: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.userData.isTunnel = true;
    mesh.renderOrder = 2;
    this.tunnelMeshes.push(mesh);
    this.group.add(mesh);
  }

  private addBridge(edge: Network['edges'][number], nodeHeights: Map<string, number>) {
    const pts = edgeTo3D(edge, nodeHeights);
    const vec = pts.map((p) => new THREE.Vector3(p.x, p.y - 1.5, p.z));
    const curve = new THREE.CatmullRomCurve3(vec, false, 'catmullrom', 0.5);
    const geo = new THREE.TubeGeometry(curve, Math.max(8, pts.length * 2), 3.2, 8, false);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x8a7550,
      transparent: true,
      opacity: 0.35,
      roughness: 1,
      metalness: 0,
    });
    const mesh = new THREE.Mesh(geo, mat);
    this.group.add(mesh);
  }
}
